import { TASK_LABELS, TASK_PRIORITY } from "./constants";
import {
  formatAppointmentService,
  formatDuration,
  formatSlotTime,
  parseSlotHour,
} from "./formatters";
import { FaCheckCircle, FaFileAlt, FaBan, FaPhoneSlash, FaInfoCircle } from 'react-icons/fa';

export function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function callKey(call, index = 0) {
  return String(call?.id ?? `${call?.phone_number || "call"}-${index}`);
}

export function leadKey(lead, index = 0) {
  return String(
    lead?.id ??
      `${lead?.phone || "no-phone"}-${lead?.email || "no-email"}-${index}`,
  );
}

function sanitizeLeadSummary(text) {
  if (!text) return "";
  const raw = String(text).trim();

  if (/^TwirpError/i.test(raw) || /sip\s*status[:\s]+480/i.test(raw)) {
    return "Call attempt failed — line temporarily unavailable";
  }
  if (/sip\s*status[:\s]+486/i.test(raw) || /busy/i.test(raw) && /sip/i.test(raw)) {
    return "Call attempt failed — line busy";
  }
  if (/sip\s*status[:\s]+408/i.test(raw) || /request.?timeout/i.test(raw)) {
    return "Call attempt failed — no response";
  }
  if (/sip\s*status[:\s]+404/i.test(raw)) {
    return "Call attempt failed — number not found";
  }
  if (/sip\s*status[:\s]+5\d\d/i.test(raw)) {
    return "Call attempt failed — server error";
  }
  if (/^TwirpError/i.test(raw)) {
    return "Call attempt failed";
  }
  if (/^pending=\d+$/i.test(raw)) {
    return "Pending";
  }
  if (/^calling\s*now$/i.test(raw)) {
    return "Currently calling";
  }
  if (raw.length > 120) {
    return `${raw.slice(0, 117)}...`;
  }
  return raw;
}

export function normalizeLeads(leads = []) {
  return leads.map((lead, index) => ({
    ...lead,
    summary_text: sanitizeLeadSummary(lead.summary_text),
    __key: leadKey(lead, index),
  }));
}

export function getCallStatusMeta(call = {}) {
  const bookingStatus = String(call.booking_status || "").toLowerCase();
  const disposition = String(call.disposition || "").toLowerCase();
  const status = bookingStatus || disposition;

  if (call.booking_confirmed || status === "booked" || /^(converted|qualified|won|sale|successful)$/.test(status)) {
    return { variant: "booked", icon: "✓", label: "Booked" };
  }

  if (status === "requested" || /^(callback_requested|interested|follow_up)$/.test(status)) {
    return { variant: "requested", icon: "📝", label: "Requested" };
  }

  if (status === "cancelled" || status === "failed" || status === "no_answer") {
    return { variant: "cancelled", icon: "✕", label: "Cancelled" };
  }

  if (call.duration_seconds != null && Number(call.duration_seconds) < 10) {
    return { variant: "dropped", icon: "✕", label: "Dropped" };
  }

  return { variant: "info", icon: "💬", label: "Info" };
}

export function getCallDirectionMeta(call = {}) {
  const direction = String(call.call_direction || call.direction || "inbound").toLowerCase();

  if (direction === "outbound") {
    return { variant: "outbound", icon: "↑", label: "Out" };
  }

  return { variant: "inbound", icon: "↓", label: "In" };
}

export function getAppointmentStatusMeta(status) {
  const normalized = String(status || "requested").toLowerCase();

  if (normalized === "booked") {
    return { variant: "booked", icon: "✓", label: "Confirmed" };
  }

  if (normalized === "cancelled") {
    return { variant: "cancelled", icon: "✕", label: "Cancelled" };
  }

  return { variant: "requested", icon: "📝", label: "Requested" };
}

export function appointmentStatusColors(status) {
  const normalized = String(status || "requested").toLowerCase();

  if (normalized === "booked") {
    return {
      backgroundColor: "rgba(22,163,74,.18)",
      borderColor: "rgba(22,163,74,.35)",
      textColor: "#1a1c22",
    };
  }

  if (normalized === "cancelled") {
    return {
      backgroundColor: "rgba(220,38,38,.14)",
      borderColor: "rgba(220,38,38,.28)",
      textColor: "#1a1c22",
    };
  }

  return {
    backgroundColor: "rgba(217,119,6,.18)",
    borderColor: "rgba(217,119,6,.34)",
    textColor: "#1a1c22",
  };
}

export function getSourceBadgeMeta(source) {
  const normalized = String(source || "call").toLowerCase();

  if (normalized === "web") {
    return { variant: "blue", label: normalized.toUpperCase() };
  }

  if (normalized === "dashboard") {
    return { variant: "purple", label: normalized.toUpperCase() };
  }

  return { variant: "gold", label: normalized.toUpperCase() };
}

export function getLeadScoreClass(score) {
  if (score == null) {
    return "";
  }

  if (Number(score) >= 61) {
    return "score-high";
  }

  if (Number(score) >= 31) {
    return "score-mid";
  }

  return "score-low";
}

export function sortQueueLeads(leads = []) {
  return [...leads].sort(
    (left, right) =>
      (TASK_PRIORITY[left.pam_task] ?? 9) - (TASK_PRIORITY[right.pam_task] ?? 9) ||
      (right.lead_score || 0) - (left.lead_score || 0),
  );
}

export function sortLeadManagementRows(leads = []) {
  return [...leads].sort(
    (left, right) => (right.lead_score || 0) - (left.lead_score || 0),
  );
}

export function outboundReasonForLead(lead) {
  if (lead?.pam_task === "confirm_appointment") {
    return "appointment confirmation";
  }

  if (lead?.pam_task === "call_back_at_time") {
    return "promised callback";
  }

  return "follow-up";
}

export function groupRescheduleSlots(slots = []) {
  const morning = [];
  const afternoon = [];
  const evening = [];

  slots.forEach((slot) => {
    const hour = parseSlotHour(slot);

    if (Number.isNaN(hour)) {
      return;
    }

    if (hour < 12) {
      morning.push(slot);
      return;
    }

    if (hour < 17) {
      afternoon.push(slot);
      return;
    }

    evening.push(slot);
  });

  return [
    { key: "morning", label: "Morning", slots: morning },
    { key: "afternoon", label: "Afternoon", slots: afternoon },
    { key: "evening", label: "Evening", slots: evening },
  ].filter((group) => group.slots.length);
}

export function parseTranscript(transcript) {
  const text =
    typeof transcript === "string"
      ? transcript
      : JSON.stringify(transcript || "", null, 2);

  try {
    const parsed = JSON.parse(text);

    if (Array.isArray(parsed) && parsed[0]?.speaker) {
      return {
        type: "structured",
        items: parsed.map((line) => {
          const isPam = /pam|calling|ai|assistant|bot/i.test(line.speaker || "");

          return {
            speaker: isPam ? "Calling AI" : "Caller",
            speakerClass: isPam ? "pam" : "caller",
            text: line.text || "",
          };
        }),
      };
    }
  } catch (error) {
    /* noop */
  }

  const lines = text.split("\n").filter((line) => line.trim());

  if (lines.length > 1 && lines[0].includes(":")) {
    return {
      type: "lines",
      items: lines.map((line) => {
        const separatorIndex = line.indexOf(":");

        if (separatorIndex < 0) {
          return {
            speaker: "--",
            speakerClass: "caller",
            text: line,
          };
        }

        const speaker = line.slice(0, separatorIndex).trim();
        const message = line.slice(separatorIndex + 1).trim();
        const isPam = /pam|calling|ai|assistant|bot/i.test(speaker);

        return {
          speaker: isPam ? "Calling AI" : speaker,
          speakerClass: isPam ? "pam" : "caller",
          text: message,
        };
      }),
    };
  }

  return { type: "raw", text };
}

export function buildCallsPerDaySeries(calls = []) {
  const days = {};
  const today = new Date();

  for (let index = 13; index >= 0; index -= 1) {
    const day = new Date(today);
    day.setDate(day.getDate() - index);
    days[day.toISOString().slice(0, 10)] = 0;
  }

  calls.forEach((call) => {
    if (!call.created_at) {
      return;
    }

    const key = String(call.created_at).slice(0, 10);

    if (key in days) {
      days[key] += 1;
    }
  });

  const entries = Object.entries(days);

  return {
    labels: entries.map(([date]) => date.slice(5)),
    values: entries.map(([, count]) => count),
  };
}

export function buildDurationBuckets(calls = []) {
  const buckets = [
    { label: "<10s", lo: 0, hi: 10, count: 0 },
    { label: "10-30s", lo: 10, hi: 30, count: 0 },
    { label: "30s-1m", lo: 30, hi: 60, count: 0 },
    { label: "1-3m", lo: 60, hi: 180, count: 0 },
    { label: "3-5m", lo: 180, hi: 300, count: 0 },
    { label: "5m+", lo: 300, hi: Number.MAX_SAFE_INTEGER, count: 0 },
  ];

  calls.forEach((call) => {
    const duration = Number(call.duration_seconds || 0);
    const bucket = buckets.find(
      (item) => duration >= item.lo && duration < item.hi,
    );

    if (bucket) {
      bucket.count += 1;
    }
  });

  return {
    labels: buckets.map((bucket) => bucket.label),
    values: buckets.map((bucket) => bucket.count),
  };
}

export function buildOutcomeSeries(stats = {}) {
  const booked = stats.total_bookings || 0;
  const requested = stats.total_booking_requests || 0;
  const dropped = stats.dropped_calls || 0;
  const info = Math.max(
    0,
    (stats.total_calls || 0) - booked - requested - dropped,
  );

  return {
    labels: ["Booked", "Requested", "Info Only", "Dropped"],
    values: [booked, requested, info, dropped],
    total: booked + requested + info + dropped || 1,
  };
}

export function buildLeadTaskSeries(leads = []) {
  const counts = {};
  const colorMap = {
    confirm_appointment: "#16a34a",
    call_back_at_time: "#2563eb",
    interested: "#7c3aed",
    follow_up: "#d97706",
    unknown: "#9ca3af",
  };

  leads.forEach((lead) => {
    const task = lead.pam_task || "unknown";
    counts[task] = (counts[task] || 0) + 1;
  });

  const items = Object.entries(counts).sort((left, right) => right[1] - left[1]);

  return {
    labels: items.map(([task]) => TASK_LABELS[task] || task),
    values: items.map(([, count]) => count),
    colors: items.map(([task]) => colorMap[task] || "#9ca3af"),
    total: leads.length || 1,
  };
}

export function getCampaignStatusMeta(status) {
  const map = {
    draft: { variant: "info", label: "Draft" },
    scheduled: { variant: "requested", label: "Scheduled" },
    running: { variant: "booked", label: "Running" },
    paused: { variant: "requested", label: "Paused" },
    completed: { variant: "gold", label: "Completed" },
    cancelled: { variant: "cancelled", label: "Cancelled" },
  };

  return map[status] || { variant: "info", label: status || "Unknown" };
}

export function getCampaignOutcomeMeta(outcome) {
  const map = {
    booked: { variant: "booked", label: "Booked" },
    interested: { variant: "teal", label: "Interested" },
    declined: { variant: "cancelled", label: "Declined" },
    not_interested: { variant: "cancelled", label: "Not Interested" },
    callback_requested: { variant: "requested", label: "Callback" },
    no_answer: { variant: "info", label: "No Answer" },
    inconclusive: { variant: "info", label: "Inconclusive" },
  };

  return map[outcome] || { variant: "info", label: outcome || "--" };
}

export function getCampaignCallStatusMeta(status) {
  const map = {
    pending: { variant: "info", label: "Pending" },
    calling: { variant: "requested", label: "Calling" },
    in_progress: { variant: "requested", label: "In Progress" },
    completed: { variant: "booked", label: "Completed" },
    no_answer: { variant: "info", label: "No Answer" },
    busy: { variant: "requested", label: "Busy" },
    failed: { variant: "cancelled", label: "Failed" },
    skipped: { variant: "info", label: "Skipped" },
  };

  return map[status] || { variant: "info", label: status || "Unknown" };
}

export function buildCallSummaryText(call = {}) {
  if (
    (call.booking_confirmed ||
      String(call.booking_status || "").toLowerCase() === "booked") &&
    call.confirmation_number
  ) {
    return {
      tone: "success",
      text: `Appointment Confirmed · Ref: ${call.confirmation_number}`,
    };
  }

  if (String(call.booking_status || "").toLowerCase() === "requested") {
    return {
      tone: "request",
      text: `Appointment Requested${call.booking_request_id ? ` · Request: ${call.booking_request_id}` : ""}`,
    };
  }

  return null;
}

export function getStatCards(stats = {}) {
  const totalCalls = Number(stats.total_calls || 0);
  const totalBookings = Number(stats.total_bookings || 0);
  const totalRequested = Number(stats.total_booking_requests || 0);
  const avgDuration = Number(stats.avg_duration || 0);
  const bookingRate = Number(stats.booking_rate || 0);
  const inboundCalls = Number(stats.inbound_calls ?? stats.total_calls ?? 0);
  const outboundCalls = Number(stats.outbound_calls || 0);
  const droppedCalls = Number(stats.dropped_calls || 0);

  return [
    {
      key: "total",
      label: "Total Calls",
      value: totalCalls,
      animate: true,
      sub: totalCalls > 0 ? `${totalCalls} calls total` : "0 calls total",
      tone: "gold",
    },
    {
      key: "bookings",
      label: "Appointments Booked",
      value: totalBookings,
      animate: true,
      sub:
        totalBookings > 0 || totalRequested > 0
          ? `${totalBookings} confirmed${totalRequested ? ` · ${totalRequested} requested` : ""}`
          : "0 confirmed",
      tone: "green",
    },
    {
      key: "duration",
      label: "Avg Call Duration",
      value: formatDuration(avgDuration),
      sub: avgDuration > 0 ? "Per conversation" : "No duration data",
      tone: "blue",
    },
    {
      key: "rate",
      label: "Booking Rate",
      value: `${Math.round(bookingRate)}%`,
      sub:
        bookingRate >= 70
          ? "Excellent"
          : bookingRate >= 40
            ? "Good"
            : bookingRate > 0
              ? "Needs improvement"
              : "No data yet",
      tone: "amber",
    },
    {
      key: "inbound",
      label: "Inbound Calls",
      value: inboundCalls,
      animate: true,
      sub: "Received calls",
      tone: "blue",
    },
    {
      key: "outbound",
      label: "Outbound Calls",
      value: outboundCalls,
      animate: true,
      sub: "Calls placed by Calling AI",
      tone: "purple",
    },
    {
      key: "dropped",
      label: "Dropped (<10s)",
      value: droppedCalls,
      animate: true,
      sub: "Under 10 seconds",
      tone: "red",
    },
  ];
}

export function getAppointmentDetailCards(appointment = {}) {
  return [
    { label: "Patient", value: appointment.patient_name || "Unknown patient" },
    { label: "Phone", value: appointment.phone || "--", type: "phone" },
    { label: "Date", value: appointment.date || "--" },
    { label: "Time", value: formatSlotTime(appointment.time || "") },
    {
      label: "Service",
      value: formatAppointmentService(appointment.service_type),
    },
    {
      label: "Status",
      value: getAppointmentStatusMeta(appointment.status),
      type: "badge",
    },
    {
      label: "Source",
      value: getSourceBadgeMeta(appointment.booking_source),
      type: "source",
    },
    { label: "Request ID", value: appointment.request_id || "--" },
    {
      label: "Confirmation",
      value: appointment.confirmation_number || "--",
    },
  ];
}

export function getCampaignProgress(campaign = {}) {
  const totalTargets = Number(campaign.total_targets || 0);
  const callsMade = Number(campaign.calls_made || 0);
  const percentage = totalTargets
    ? Math.round((callsMade / totalTargets) * 100)
    : 0;

  return {
    percentage,
    label: `${callsMade}/${totalTargets}`,
  };
}

export function formatCampaignCallDuration(seconds) {
  if (!seconds) {
    return "--";
  }

  return formatDuration(seconds);
}

export function formatLeadTaskLabel(task) {
  return TASK_LABELS[task] || task || "--";
}
